// Admin Security Utilities
import { v4 as uuidv4 } from "uuid";

// Secure admin route generation and validation
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const MAX_LOGIN_ATTEMPTS = 3;
const LOCKOUT_TIME = 15 * 60 * 1000; // 15 minutes
const TWO_FA_REMEMBER_TIME = 30 * 60 * 1000; // 30 minutes for 2FA remember

// Generate or get secure admin route (simplified)
export const getSecureAdminRoute = () => {
  // Simple but not obvious route - better than /admin but not overly complex
  return "admin-panel";
};

// Validate admin route
export const isValidAdminRoute = (route) => {
  const validRoute = getSecureAdminRoute();
  return route === validRoute;
};

// Session management
export const setAdminSession = (user, userData = null) => {
  const sessionData = {
    user: {
      uid: user.uid,
      email: user.email,
      role: userData?.role || 'admin', // Include role in session
    },
    timestamp: Date.now(),
    expiresAt: Date.now() + SESSION_TIMEOUT,
  };

  sessionStorage.setItem("admin_session", JSON.stringify(sessionData));

  // Clear any previous login attempts
  clearLoginAttempts();
};

export const getAdminSession = () => {
  const sessionData = sessionStorage.getItem("admin_session");
  if (!sessionData) return null;

  try {
    const session = JSON.parse(sessionData);

    // Check if session is expired
    if (Date.now() > session.expiresAt) {
      clearAdminSession();
      return null;
    }

    return session;
  } catch (error) {
    clearAdminSession();
    return null;
  }
};

export const clearAdminSession = () => {
  sessionStorage.removeItem("admin_session");
};

export const isAdminSessionValid = () => {
  const session = getAdminSession();
  return session !== null;
};

// Get current user role from session
export const getCurrentUserRole = () => {
  const session = getAdminSession();
  return session?.user?.role || 'admin';
};

// Get current user info from session
export const getCurrentUserInfo = () => {
  const session = getAdminSession();
  return session?.user || null;
};

// Extend session on activity
export const extendAdminSession = () => {
  const session = getAdminSession();
  if (session) {
    session.expiresAt = Date.now() + SESSION_TIMEOUT;
    sessionStorage.setItem("admin_session", JSON.stringify(session));
  }
};

// Login attempt tracking for brute force protection
export const trackLoginAttempt = (email) => {
  const attempts = getLoginAttempts(email);
  attempts.count += 1;
  attempts.lastAttempt = Date.now();

  localStorage.setItem(`login_attempts_${email}`, JSON.stringify(attempts));

  return attempts;
};

export const getLoginAttempts = (email) => {
  const stored = localStorage.getItem(`login_attempts_${email}`);
  if (!stored) {
    return { count: 0, lastAttempt: 0, lockedUntil: 0 };
  }

  try {
    return JSON.parse(stored);
  } catch {
    return { count: 0, lastAttempt: 0, lockedUntil: 0 };
  }
};

export const isAccountLocked = (email) => {
  const attempts = getLoginAttempts(email);

  // If account is locked, check if lockout period has passed
  if (attempts.lockedUntil && Date.now() < attempts.lockedUntil) {
    return true;
  }

  // If lockout period has passed, reset attempts
  if (attempts.lockedUntil && Date.now() >= attempts.lockedUntil) {
    clearLoginAttempts(email);
    return false;
  }

  // Check if max attempts reached
  if (attempts.count >= MAX_LOGIN_ATTEMPTS) {
    // Lock the account
    attempts.lockedUntil = Date.now() + LOCKOUT_TIME;
    localStorage.setItem(`login_attempts_${email}`, JSON.stringify(attempts));
    return true;
  }

  return false;
};

export const clearLoginAttempts = (email = null) => {
  if (email) {
    localStorage.removeItem(`login_attempts_${email}`);
  } else {
    // Clear all login attempts (on successful login)
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith("login_attempts_")) {
        localStorage.removeItem(key);
      }
    });
  }
};

export const getRemainingLockoutTime = (email) => {
  const attempts = getLoginAttempts(email);
  if (attempts.lockedUntil && Date.now() < attempts.lockedUntil) {
    return Math.ceil((attempts.lockedUntil - Date.now()) / 1000 / 60); // minutes
  }
  return 0;
};

// Security headers and CSRF protection
export const generateCSRFToken = () => {
  const token = uuidv4();
  sessionStorage.setItem("csrf_token", token);
  return token;
};

export const validateCSRFToken = (token) => {
  const storedToken = sessionStorage.getItem("csrf_token");
  return token === storedToken;
};

// Admin activity logging - Enhanced version with Firebase integration
export const logAdminActivity = async (action, details = {}) => {
  const session = getAdminSession();
  if (!session) return;

  const logEntry = {
    timestamp: new Date().toISOString(),
    user: session.user.email,
    action,
    details,
    ip: "client-side", // In a real app, this would be server-side
    userAgent: navigator.userAgent,
  };

  // Store in sessionStorage for immediate access (fallback)
  const logs = JSON.parse(
    sessionStorage.getItem("admin_activity_logs") || "[]"
  );
  logs.push(logEntry);

  // Keep only last 100 logs
  if (logs.length > 100) {
    logs.splice(0, logs.length - 100);
  }

  sessionStorage.setItem("admin_activity_logs", JSON.stringify(logs));
  console.log("Admin Activity:", logEntry);

  // Send to Firebase for persistent logging (async, don't wait)
  try {
    // Import Firebase function dynamically to avoid circular imports
    const { logAdminActivity: firebaseLogActivity } = await import(
      "../firebase"
    );
    firebaseLogActivity(action, details);
  } catch (error) {
    console.error("Failed to send activity to Firebase:", error);
  }
};

export const getAdminActivityLogs = () => {
  return JSON.parse(sessionStorage.getItem("admin_activity_logs") || "[]");
};

// Clear all admin security data
export const clearAllAdminData = () => {
  clearAdminSession();
  clear2FARememberToken();
  sessionStorage.removeItem("csrf_token");
  sessionStorage.removeItem("admin_activity_logs");
};

// Auto-logout on inactivity
let inactivityTimer = null;

export const startInactivityTimer = (onTimeout) => {
  clearInactivityTimer();

  inactivityTimer = setTimeout(() => {
    logAdminActivity("auto_logout", { reason: "inactivity" });
    clearAllAdminData();
    onTimeout();
  }, SESSION_TIMEOUT);
};

export const resetInactivityTimer = (onTimeout) => {
  if (isAdminSessionValid()) {
    extendAdminSession();
    startInactivityTimer(onTimeout);
  }
};

export const clearInactivityTimer = () => {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }
};

// Environment-specific security settings
export const getSecurityConfig = () => {
  const isDevelopment = process.env.NODE_ENV === "development";

  return {
    sessionTimeout: isDevelopment ? 60 * 60 * 1000 : SESSION_TIMEOUT, // 1 hour in dev
    maxLoginAttempts: isDevelopment ? 5 : MAX_LOGIN_ATTEMPTS,
    lockoutTime: isDevelopment ? 5 * 60 * 1000 : LOCKOUT_TIME, // 5 min in dev
    enableLogging: true,
  };
};

// 2FA Remember functionality - stores a secure token to skip 2FA for 30 minutes
export const set2FARememberToken = (userEmail, uid) => {
  const rememberData = {
    userEmail,
    uid,
    token: uuidv4(), // Secure random token
    timestamp: Date.now(),
    expiresAt: Date.now() + TWO_FA_REMEMBER_TIME,
  };

  sessionStorage.setItem("2fa_remember_token", JSON.stringify(rememberData));

  logAdminActivity("2fa_remember_token_set", {
    userEmail,
    uid,
    timestamp: new Date().toISOString(),
    expiresAt: new Date(rememberData.expiresAt).toISOString(),
  });

  return rememberData.token;
};

export const get2FARememberToken = () => {
  const rememberData = sessionStorage.getItem("2fa_remember_token");
  if (!rememberData) return null;

  try {
    const parsed = JSON.parse(rememberData);

    // Check if token is expired
    if (Date.now() > parsed.expiresAt) {
      clear2FARememberToken();
      return null;
    }

    return parsed;
  } catch (error) {
    clear2FARememberToken();
    return null;
  }
};

export const is2FARemembered = (userEmail, uid) => {
  const rememberData = get2FARememberToken();
  if (!rememberData) return false;

  // Check if it matches the current user
  const matches =
    rememberData.userEmail === userEmail && rememberData.uid === uid;

  if (!matches) {
    clear2FARememberToken(); // Clear if it's for a different user
    return false;
  }

  // Check if expired
  if (Date.now() > rememberData.expiresAt) {
    clear2FARememberToken();
    return false;
  }

  logAdminActivity("2fa_remember_token_used", {
    userEmail,
    uid,
    timestamp: new Date().toISOString(),
  });

  return true;
};

export const clear2FARememberToken = () => {
  const rememberData = get2FARememberToken();
  if (rememberData) {
    logAdminActivity("2fa_remember_token_cleared", {
      userEmail: rememberData.userEmail,
      timestamp: new Date().toISOString(),
    });
  }

  sessionStorage.removeItem("2fa_remember_token");
};

export const extend2FARememberToken = () => {
  const rememberData = get2FARememberToken();
  if (rememberData) {
    rememberData.expiresAt = Date.now() + TWO_FA_REMEMBER_TIME;
    sessionStorage.setItem("2fa_remember_token", JSON.stringify(rememberData));

    logAdminActivity("2fa_remember_token_extended", {
      userEmail: rememberData.userEmail,
      timestamp: new Date().toISOString(),
      newExpiresAt: new Date(rememberData.expiresAt).toISOString(),
    });
  }
};
